// ---------------------------------------------------------------------------
// Accessify – Bildbeschreibung (Image Description) Feature
// ---------------------------------------------------------------------------
//
//  A) AUTO-APPLY (runs on widget init, no feature activation needed):
//     Loads alt texts from server manifest + IndexedDB cache and silently
//     applies them to images (alt + title). Ensures all visitors get
//     alt texts immediately.
//
//  B) GENERATE (when user activates the feature toggle):
//     Scans for images without alt text → generates via AI → injects
//     alt + title into DOM. Title attribute provides native hover tooltip
//     on every image, consistently.
//
// Framer compatibility:
//   - Uses currentSrc || src (srcset-aware)
//   - Treats empty alt="" on large images as "missing" (Framer sets alt="" on all)
//   - Delayed re-scans for lazy-loaded images
//   - MutationObserver for dynamically added images
// ---------------------------------------------------------------------------

import type { FeatureModule, FeatureState, AIService } from '../types';
import { RateLimitError } from '../services/ai-service';
import { getCurrentWidgetLang } from '../i18n/index';
import { pageLocation } from '../preview-url';

// ---------------------------------------------------------------------------
// IndexedDB cache
// ---------------------------------------------------------------------------

const IDB_NAME = 'accessify-alt-text-cache';
const IDB_STORE = 'alt-texts';
const IDB_VERSION = 2;

interface CachedAltText {
  key: string; src: string; altText: string; lang: string; createdAt: number;
}

function hashSrc(src: string): string {
  let hash = 5381;
  const n = src.toLowerCase().trim();
  for (let i = 0; i < n.length; i++) { hash = ((hash << 5) + hash) + n.charCodeAt(i); hash |= 0; }
  return Math.abs(hash).toString(36);
}

/** Normalize image URL: strip query params for cache matching (Framer adds ?width=&height=) */
function normalizeImageUrl(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.delete('width');
    u.searchParams.delete('height');
    u.searchParams.delete('scale-down-to');
    return u.href;
  } catch {
    return url;
  }
}

/** Get the best available src for an image (Framer srcset aware) */
function getImageSrc(img: HTMLImageElement): string {
  return img.currentSrc || img.src;
}

function openCache(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: 'key' });
        if (!db.objectStoreNames.contains('alt-text-reports')) db.createObjectStore('alt-text-reports', { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function getAllCachedAltTexts(): Promise<Map<string, string>> {
  const db = await openCache();
  const map = new Map<string, string>();
  if (!db) return map;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).getAll();
      req.onsuccess = () => {
        for (const r of req.result as CachedAltText[]) { map.set(r.src, r.altText); }
        resolve(map);
      };
      req.onerror = () => resolve(map);
    } catch { resolve(map); }
  });
}

async function getCachedAltText(src: string): Promise<string | null> {
  const db = await openCache();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req1 = store.get(hashSrc(src));
      req1.onsuccess = () => {
        if ((req1.result as CachedAltText | undefined)?.altText) {
          resolve(req1.result.altText);
          return;
        }
        const norm = normalizeImageUrl(src);
        if (norm !== src) {
          const req2 = store.get(hashSrc(norm));
          req2.onsuccess = () => resolve((req2.result as CachedAltText | undefined)?.altText || null);
          req2.onerror = () => resolve(null);
        } else {
          resolve(null);
        }
      };
      req1.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function setCachedAltText(src: string, altText: string, langCode: string): Promise<void> {
  const db = await openCache();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put({ key: hashSrc(src), src, altText, lang: langCode, createdAt: Date.now() } as CachedAltText);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch { resolve(); }
  });
}

// ---------------------------------------------------------------------------
// Server-side alt-text cache (cross-browser, cross-visitor)
// ---------------------------------------------------------------------------

const DEFAULT_API_BASE = 'https://api.goaccessify.de';

// Module-wide mode tracking, set on every manifest fetch. 'manual' = default,
// never run live AI for unknown images. 'auto' = live fallback + auto-persist OK.
type ResimplifyMode = 'manual' | 'auto';
let currentAltSiteMode: ResimplifyMode = 'manual';

async function fetchServerAltTexts(siteKey: string, proxyUrl: string, lang: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  try {
    const base = proxyUrl || DEFAULT_API_BASE;
    const { origin, pathname } = pageLocation();
    const pageUrl = origin + pathname;
    const res = await fetch(`${base}/v1/manifest?siteKey=${encodeURIComponent(siteKey)}&url=${encodeURIComponent(pageUrl)}&feature=alt_text&variant=${encodeURIComponent(lang)}`, {
      headers: { 'Accept': 'application/json' },
      cache: 'no-cache',
    });
    if (!res.ok) return map;
    const data = await res.json() as { blocks?: Array<{ selector: string; result: string }>; siteMode?: ResimplifyMode };
    // Update authoritative mode from server on every fetch
    currentAltSiteMode = data.siteMode === 'auto' ? 'auto' : 'manual';
    if (data.blocks) {
      for (const block of data.blocks) {
        if (block.selector && block.result) map.set(block.selector, block.result);
      }
    }
  } catch { /* fall back to IndexedDB */ }
  return map;
}

function persistAltTextToServer(siteKey: string, proxyUrl: string, imageUrl: string, altText: string, lang: string): void {
  try {
    const base = proxyUrl || DEFAULT_API_BASE;
    const { origin, pathname } = pageLocation();
    const pageUrl = origin + pathname;
    fetch(`${base}/v1/cache/persist-alt-text`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteKey, pageUrl, imageUrl, altText, lang }),
    }).catch(() => {});
  } catch { /* fire-and-forget */ }
}

// ---------------------------------------------------------------------------
// Auto-apply: runs on widget init, no feature activation needed
// ---------------------------------------------------------------------------

let autoApplied = false;

/** Silently apply cached/manifest alt texts to all images on the page */
export async function autoApplyCachedAltTexts(
  widgetConfig?: { siteKey?: string; proxyUrl?: string; lang?: string },
): Promise<number> {
  if (autoApplied) return 0;
  autoApplied = true;

  const cache = new Map<string, string>();
  const siteKey = widgetConfig?.siteKey;
  const proxyUrl = widgetConfig?.proxyUrl || '';
  const pageLang = widgetConfig?.lang?.split('-')[0] || document.documentElement.lang?.split('-')[0] || 'en';

  // 1. Server manifest (freshest, cross-browser)
  if (siteKey) {
    const serverCache = await fetchServerAltTexts(siteKey, proxyUrl, pageLang);
    for (const [url, alt] of serverCache) {
      // Store BOTH original URL and normalized URL (without width/height params)
      // so lookups by currentSrc (any size) will match
      cache.set(url, alt);
      const norm = normalizeImageUrl(url);
      if (norm !== url) cache.set(norm, alt);
      setCachedAltText(url, alt, pageLang).catch(() => {});
    }
  }

  // 2. IndexedDB (fills gaps)
  const localCache = await getAllCachedAltTexts();
  for (const [url, alt] of localCache) {
    if (!cache.has(url)) cache.set(url, alt);
  }

  if (cache.size === 0) return 0;

  function applyToImg(img: HTMLImageElement): boolean {
    if (img.closest('#accessify-root') || img.closest('accessify-widget')) return false;
    const src = getImageSrc(img);
    const norm = normalizeImageUrl(src);
    const imgSrcNorm = normalizeImageUrl(img.src);
    const cached = cache.get(src) || cache.get(norm) || cache.get(img.src) || cache.get(imgSrcNorm);
    if (!cached) return false;

    const alt = img.getAttribute('alt');
    if (alt === null || alt.trim() === '') {
      img.setAttribute('alt', cached);
      img.setAttribute('data-accessify-alt', 'auto');
    }
    // Always set title for native hover tooltip
    img.setAttribute('title', cached);
    return true;
  }

  let applied = 0;
  document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
    if (applyToImg(img)) applied++;
  });

  // Watch for late-loading images (Framer SPA, lazy load)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        const imgs = node instanceof HTMLImageElement ? [node] :
          node instanceof HTMLElement ? Array.from(node.querySelectorAll<HTMLImageElement>('img')) : [];
        for (const img of imgs) applyToImg(img);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 30000);

  return applied;
}

// ---------------------------------------------------------------------------
// Feature Module
// ---------------------------------------------------------------------------

export default function createAltTextModule(
  aiService?: AIService,
  initialLang: string = 'de',
  serverConfig?: { siteKey?: string; proxyUrl?: string },
): FeatureModule {
  const siteKey = serverConfig?.siteKey || '';
  const proxyUrl = serverConfig?.proxyUrl || '';
  let enabled = false;
  let domObserver: MutationObserver | null = null;
  let autoGenerating = false;

  // Tracked images
  let missingAltImages: HTMLImageElement[] = [];
  const processedImages = new Map<HTMLImageElement, { generatedAlt: string }>();

  function lang(): string { return getCurrentWidgetLang() || initialLang; }
  function isDE(): boolean { return lang().startsWith('de'); }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function isGenericAlt(alt: string): boolean {
    if (!alt.trim()) return true;
    if (/^(image|img|photo|bild|foto|untitled|placeholder)/i.test(alt)) return true;
    if (/^IMG_\d+/i.test(alt)) return true;
    if (/\.(jpg|jpeg|png|gif|webp|svg|avif)$/i.test(alt)) return true;
    return false;
  }

  function isDecorativeImage(img: HTMLImageElement): boolean {
    const role = img.getAttribute('role');
    if (role === 'presentation' || role === 'none') return true;
    if (img.complete && img.naturalWidth > 0 && (img.naturalWidth < 50 || img.naturalHeight < 50)) return true;
    return false;
  }

  function isValidImage(img: HTMLImageElement): boolean {
    if (img.closest('#accessify-root') || img.closest('accessify-widget')) return false;
    // Skip tiny images (icons, tracking pixels, avatars)
    if (img.complete && img.naturalWidth > 0 && (img.naturalWidth < 50 || img.naturalHeight < 50)) return false;
    // Skip images not rendered on-screen (display:none, visibility:hidden, etc.)
    const rect = img.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 40) return false;
    // Skip images hidden via CSS (opacity:0, visibility:hidden)
    const style = getComputedStyle(img);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    // Skip images with role="presentation" or aria-hidden
    if (img.getAttribute('role') === 'presentation' || img.getAttribute('aria-hidden') === 'true') return false;
    return true;
  }

  function needsAltText(img: HTMLImageElement): boolean {
    if (!isValidImage(img)) return false;
    const alt = img.getAttribute('alt');
    if (alt === null) return true;
    if (isGenericAlt(alt)) return true;
    if (alt === '' && !isDecorativeImage(img)) return true;
    return false;
  }

  function scanForMissingAlt(): HTMLImageElement[] {
    const missing: HTMLImageElement[] = [];
    document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (needsAltText(img)) missing.push(img);
    });
    document.querySelectorAll<HTMLElement>('picture').forEach((picture) => {
      if (picture.closest('#accessify-root') || picture.closest('accessify-widget')) return;
      const img = picture.querySelector('img');
      if (img && !missing.includes(img) && needsAltText(img)) {
        missing.push(img);
      }
    });
    return missing;
  }

  function gatherImageContext(img: HTMLImageElement): string {
    const parts: string[] = [];
    try {
      const url = new URL(getImageSrc(img), pageLocation().href);
      const filename = url.pathname.split('/').pop();
      if (filename) parts.push(`Filename: ${filename}`);
    } catch {}
    const figure = img.closest('figure');
    if (figure) {
      const caption = figure.querySelector('figcaption');
      if (caption?.textContent?.trim()) parts.push(`Caption: ${caption.textContent.trim()}`);
    }
    const container = img.parentElement?.parentElement || img.parentElement;
    if (container) {
      const heading = container.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading?.textContent?.trim()) parts.push(`Nearby heading: ${heading.textContent.trim()}`);
    }
    return parts.join('. ');
  }

  // -------------------------------------------------------------------------
  // Apply + Generate (title = native hover tooltip on every image)
  // -------------------------------------------------------------------------

  function applyAltText(img: HTMLImageElement, altText: string) {
    img.setAttribute('alt', altText);
    img.setAttribute('title', altText);
    processedImages.set(img, { generatedAlt: altText });
    if (enabled) addInfoBadge(img, altText);
  }

  /** Set title on every visible image that has alt text but no title yet */
  function applyTitlesToAllImages() {
    document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (!isValidImage(img)) return;
      // Skip if already has a title
      if (img.getAttribute('title')) return;
      const alt = img.getAttribute('alt');
      if (alt && alt.trim()) {
        img.setAttribute('title', alt);
        img.setAttribute('data-accessify-title', 'auto');
      }
    });
  }

  // -------------------------------------------------------------------------
  // Info badge — small "i" icon on each image with alt text
  // -------------------------------------------------------------------------
  // Badges live in a single fixed-viewport overlay container on document.body.
  // The overlay is viewport-sized with overflow:hidden so that absolute-
  // positioned badges can never extend beyond the visible area. This is
  // critical on pages with off-screen content like infinite sponsor-sliders
  // or Framer marquee tickers: their images can physically live at x=8000
  // (duplicated in the slider track), and a `position:absolute; width:0;
  // height:0; overflow:visible` overlay on body let those badges reach
  // into documentElement.scrollWidth — Chrome Mobile emulation reacts by
  // expanding the layout viewport from e.g. 390 to 900+, the page
  // visually kips left and a black/empty strip appears on the right.
  //
  // Because the overlay is position:fixed, badge positions are
  // viewport-relative (rect.top / rect.left, NO scrollY / scrollX). The
  // existing rAF loop already re-runs positionBadge on every frame, so
  // scroll-tracking continues to work — rect.top simply reflects the
  // current visual position.
  // -------------------------------------------------------------------------

  const BADGE_ATTR = 'data-accessify-badge';
  const WRAPPER_ATTR = 'data-accessify-badge-wrap';
  const OVERLAY_ID = 'accessify-badge-overlay';
  let badgePositionRAF: number | null = null;
  let trackedBadges: Array<{ target: HTMLElement; badge: HTMLElement; tooltip: HTMLElement }> = [];

  function getOrCreateOverlay(): HTMLElement {
    let overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      Object.assign(overlay.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: '10000',
      });
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  function positionBadge(target: HTMLElement, badge: HTMLElement, tooltip: HTMLElement) {
    const rect = target.getBoundingClientRect();
    // Skip if element is not visible / too small / off-screen
    if (rect.width < 40 || rect.height < 40 ||
        rect.bottom < 0 || rect.top > window.innerHeight + 200 ||
        rect.right < 0 || rect.left > window.innerWidth + 200) {
      badge.style.display = 'none';
      tooltip.style.display = 'none';
      return;
    }
    // Viewport-relative (overlay is position:fixed). No scroll add-on,
    // no overflow into document width/height — even if a host site
    // renders an infinite slider with images at x=8000.
    const top = rect.top + 6;
    const left = rect.left + 6;
    badge.style.display = '';
    badge.style.top = `${top}px`;
    badge.style.left = `${left}px`;
    // Tooltip positioned below badge
    tooltip.style.top = `${top + 26}px`;
    tooltip.style.left = `${left}px`;
  }

  function updateAllBadgePositions() {
    for (const { target, badge, tooltip } of trackedBadges) {
      if (!document.body.contains(target)) continue;
      positionBadge(target, badge, tooltip);
    }
  }

  function startBadgeTracking() {
    if (badgePositionRAF !== null) return;
    const tick = () => {
      updateAllBadgePositions();
      badgePositionRAF = requestAnimationFrame(tick);
    };
    badgePositionRAF = requestAnimationFrame(tick);
  }

  function stopBadgeTracking() {
    if (badgePositionRAF !== null) {
      cancelAnimationFrame(badgePositionRAF);
      badgePositionRAF = null;
    }
  }

  function addInfoBadge(target: HTMLElement, altText: string) {
    if (target.getAttribute(BADGE_ATTR)) return;
    target.setAttribute(BADGE_ATTR, '1');

    const overlay = getOrCreateOverlay();

    // Badge "i" circle
    const badge = document.createElement('span');
    Object.assign(badge.style, {
      position: 'absolute',
      width: '22px',
      height: '22px',
      borderRadius: '50%',
      background: 'rgba(0,0,0,0.6)',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontWeight: 'bold',
      fontSize: '13px',
      lineHeight: '22px',
      textAlign: 'center',
      cursor: 'default',
      pointerEvents: 'auto',
      boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
      transition: 'background 0.15s',
    });
    badge.textContent = 'i';
    badge.setAttribute('aria-hidden', 'true');
    badge.setAttribute('translate', 'no');
    badge.classList.add('notranslate');
    badge.setAttribute(BADGE_ATTR, 'badge');

    // Tooltip
    const tooltip = document.createElement('span');
    Object.assign(tooltip.style, {
      display: 'none',
      position: 'absolute',
      minWidth: '150px',
      maxWidth: '280px',
      padding: '6px 10px',
      background: 'rgba(0,0,0,0.88)',
      color: '#fff',
      fontFamily: 'sans-serif',
      fontSize: '12px',
      lineHeight: '1.4',
      borderRadius: '6px',
      pointerEvents: 'none',
      wordWrap: 'break-word',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      zIndex: '1',
    });
    tooltip.textContent = altText;
    tooltip.setAttribute('translate', 'no');
    tooltip.classList.add('notranslate');
    tooltip.setAttribute(BADGE_ATTR, 'tooltip');

    // Mouse: show/hide on hover. Touch: toggle on tap.
    badge.addEventListener('mouseenter', () => {
      badge.style.background = 'rgba(0,0,0,0.85)';
      tooltip.style.display = 'block';
    });
    badge.addEventListener('mouseleave', () => {
      badge.style.background = 'rgba(0,0,0,0.6)';
      tooltip.style.display = 'none';
    });
    badge.addEventListener('click', (e) => {
      e.stopPropagation();
      const isVisible = tooltip.style.display === 'block';
      tooltip.style.display = isVisible ? 'none' : 'block';
      badge.style.background = isVisible ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.85)';
    });

    overlay.appendChild(badge);
    overlay.appendChild(tooltip);

    // Initial position
    positionBadge(target, badge, tooltip);

    // Track for continuous position updates
    trackedBadges.push({ target, badge, tooltip });
  }

  function removeAllBadges() {
    stopBadgeTracking();
    trackedBadges = [];
    // Remove overlay
    document.getElementById(OVERLAY_ID)?.remove();
    // Clear badge markers
    document.querySelectorAll(`[${BADGE_ATTR}]`).forEach(el => el.removeAttribute(BADGE_ATTR));
    // Legacy: unwrap any leftover wrappers from old version
    document.querySelectorAll<HTMLElement>(`[${WRAPPER_ATTR}]`).forEach(wrapper => {
      const img = wrapper.querySelector('img');
      if (img && wrapper.parentElement) {
        wrapper.parentElement.insertBefore(img, wrapper);
        wrapper.remove();
      }
    });
  }

  /** Add info badges to ALL visible images */
  function addBadgesToAllImages() {
    document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (!isValidImage(img)) return;
      const alt = img.getAttribute('alt') || img.getAttribute('title');
      if (alt && alt.trim()) {
        addInfoBadge(img, alt.trim());
      }
    });
    // Also for background images
    document.querySelectorAll<HTMLElement>('[data-accessify-bg-alt]').forEach((el) => {
      const alt = el.getAttribute('aria-label');
      if (alt && alt.trim()) {
        addInfoBadge(el, alt.trim());
      }
    });
    // Start tracking positions (handles scroll, resize, zoom)
    if (trackedBadges.length > 0) startBadgeTracking();
  }

  function removeTitles() {
    document.querySelectorAll<HTMLImageElement>('img[data-accessify-title="auto"]').forEach((img) => {
      img.removeAttribute('title');
      img.removeAttribute('data-accessify-title');
    });
    // Clean up background image descriptions
    document.querySelectorAll<HTMLElement>('[data-accessify-bg-alt]').forEach((el) => {
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
      el.removeAttribute('title');
      el.removeAttribute('data-accessify-bg-alt');
    });
    // Remove all info badges
    removeAllBadges();
  }

  // -------------------------------------------------------------------------
  // Background images (CSS background-image)
  // -------------------------------------------------------------------------

  /** Find significant elements with CSS background-image and no accessibility info */
  function scanForBackgroundImages(): HTMLElement[] {
    const found: HTMLElement[] = [];
    // Scan ALL elements — not just specific class patterns — for background-image
    const allElements = document.querySelectorAll<HTMLElement>('*');
    for (const el of allElements) {
      if (el.closest('#accessify-root') || el.closest('accessify-widget')) continue;
      if (el.getAttribute('data-accessify-bg-alt')) continue;
      // Skip tiny or invisible elements quickly via offsetHeight (avoids getComputedStyle)
      if (el.offsetWidth < 200 || el.offsetHeight < 150) continue;
      const style = getComputedStyle(el);
      const bg = style.backgroundImage;
      if (!bg || bg === 'none') continue;
      // Must have a url() reference (not just gradients)
      const urlMatch = bg.match(/url\(["']?([^"')]+)["']?\)/);
      if (!urlMatch) continue;
      // Skip SVG data URIs and tiny inline images
      if (urlMatch[1].startsWith('data:image/svg')) continue;
      // Skip if already has role="img" with aria-label
      if (el.getAttribute('role') === 'img' && el.getAttribute('aria-label')) continue;
      found.push(el);
    }
    return found;
  }

  function getBackgroundImageUrl(el: HTMLElement): string | null {
    const bg = getComputedStyle(el).backgroundImage;
    const match = bg?.match(/url\(["']?([^"')]+)["']?\)/);
    return match ? match[1] : null;
  }

  function applyBgAlt(el: HTMLElement, altText: string) {
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', altText);
    el.setAttribute('title', altText);
    el.setAttribute('data-accessify-bg-alt', 'auto');
    if (enabled) addInfoBadge(el, altText);
  }

  async function generateBgAlts() {
    if (!aiService || !enabled) return;
    if (currentAltSiteMode !== 'auto') {
      if ((window as any).__ACCESSIFY_DEBUG) {
        console.info('[Accessify] alt-text: siteMode=manual — skipping live generation for background images');
      }
      return;
    }
    const bgElements = scanForBackgroundImages();
    for (const el of bgElements) {
      if (!enabled) break;
      const src = getBackgroundImageUrl(el);
      if (!src) continue;
      // Check cache first
      const cached = await getCachedAltText(src);
      if (cached) {
        applyBgAlt(el, cached);
        continue;
      }
      // Generate via AI
      try {
        const ctx = gatherBgContext(el);
        const alt = await aiService.generateAltText(src, ctx, lang());
        if (alt && enabled) {
          applyBgAlt(el, alt);
          setCachedAltText(src, alt, lang()).catch(() => {});
          if (siteKey) persistAltTextToServer(siteKey, proxyUrl, src, alt, lang());
        }
      } catch (err) {
        console.warn('[Accessify] Bg alt-text generation failed:', src, err);
      }
    }
  }

  function gatherBgContext(el: HTMLElement): string {
    const parts: string[] = [];
    // Tag and class hints
    parts.push(`Element: <${el.tagName.toLowerCase()}>`);
    if (el.className) parts.push(`Class: ${el.className}`);
    // Text inside
    const text = el.textContent?.trim().slice(0, 100);
    if (text) parts.push(`Overlay text: ${text}`);
    // Nearby heading
    const heading = el.querySelector('h1, h2, h3');
    if (heading?.textContent?.trim()) parts.push(`Heading: ${heading.textContent.trim()}`);
    return parts.join('. ');
  }

  async function generateAll() {
    if (autoGenerating || !enabled || !aiService) return;
    autoGenerating = true;
    const CONCURRENCY = 3;

    // Phase 1: apply from IndexedDB cache
    const uncached: HTMLImageElement[] = [];
    for (const img of missingAltImages) {
      if (processedImages.has(img)) continue;
      const src = getImageSrc(img);
      const cached = await getCachedAltText(src);
      if (cached) {
        applyAltText(img, cached);
      } else {
        uncached.push(img);
      }
    }

    if (!uncached.length) { autoGenerating = false; return; }

    // SAFETY GATE: only proceed with live AI if site is in 'auto' mode.
    // In 'manual' (default), leave images without generated alt text — the
    // owner must trigger a crawl or switch modes from the dashboard.
    if (currentAltSiteMode !== 'auto') {
      if ((window as any).__ACCESSIFY_DEBUG) {
        console.info(
          `[Accessify] alt-text: siteMode=manual — ${uncached.length} image(s) left untouched. ` +
          `Trigger a crawl from the dashboard or enable auto mode.`,
        );
      }
      autoGenerating = false;
      return;
    }

    // Phase 2: AI generation in parallel batches
    for (let i = 0; i < uncached.length; i += CONCURRENCY) {
      if (!enabled) break;
      const batch = uncached.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async (img) => {
        if (!enabled) return;
        // Visible loading state during the AI roundtrip. R9-2 caught us
        // running silently for 12 calls without any user feedback.
        img.setAttribute('data-accessify-loading', 'true');
        try {
          const src = getImageSrc(img);
          const ctx = gatherImageContext(img);
          const alt = await aiService!.generateAltText(src, ctx, lang());
          if (alt && enabled) {
            img.removeAttribute('data-accessify-loading');
            img.removeAttribute('data-accessify-error');
            applyAltText(img, alt);
            setCachedAltText(src, alt, lang()).catch(() => {});
            if (siteKey) persistAltTextToServer(siteKey, proxyUrl, src, alt, lang());
          } else if (enabled) {
            // Backend returned empty result (no exception) — R9-2: this is
            // the silent-failure case the live audit caught. Surface it so
            // tests + curious users notice instead of seeing nothing.
            img.removeAttribute('data-accessify-loading');
            img.setAttribute('data-accessify-error', 'true');
            img.setAttribute(
              'title',
              (lang() === 'de' ? 'KI lieferte keine Bildbeschreibung' : 'AI returned no description'),
            );
          }
        } catch (err) {
          // R6-3 visible signal: leave a stable hook + title tooltip on the
          // image. Replacement failed, so the original (missing-alt) image
          // stays in place — but tests and curious users can now detect
          // the failure via [data-accessify-error="true"].
          img.removeAttribute('data-accessify-loading');
          img.setAttribute('data-accessify-error', 'true');
          img.setAttribute(
            'title',
            (lang() === 'de' ? 'Bildbeschreibung fehlgeschlagen' : 'Alt-text generation failed'),
          );
          console.warn('[Accessify] Alt-text generation failed:', getImageSrc(img), err);
        }
      }));
    }

    autoGenerating = false;
  }

  async function generateSingle(img: HTMLImageElement) {
    if (!aiService || !enabled) return;
    const src = getImageSrc(img);
    const cached = await getCachedAltText(src);
    if (cached) { applyAltText(img, cached); return; }

    // Mode gate — only live-generate when site is in 'auto' mode.
    // The badge stays unrendered in manual mode (cost-protection), but
    // log a debug hint so a confused dev knows why hovering over the
    // image is producing nothing.
    if (currentAltSiteMode !== 'auto') {
      if ((window as any).__ACCESSIFY_DEBUG) {
        console.info('[Accessify-DEBUG] alt-text: siteMode=manual, skipping live AI for', src);
      }
      return;
    }

    img.setAttribute('data-accessify-loading', 'true');
    try {
      const ctx = gatherImageContext(img);
      const alt = await aiService.generateAltText(src, ctx, lang());
      if (alt && enabled) {
        img.removeAttribute('data-accessify-loading');
        img.removeAttribute('data-accessify-error');
        applyAltText(img, alt);
        setCachedAltText(src, alt, lang()).catch(() => {});
        if (siteKey) persistAltTextToServer(siteKey, proxyUrl, src, alt, lang());
      } else if (enabled) {
        // Backend returned empty result — surface it instead of going silent (R9-2).
        img.removeAttribute('data-accessify-loading');
        img.setAttribute('data-accessify-error', 'true');
        img.setAttribute(
          'title',
          (lang() === 'de' ? 'KI lieferte keine Bildbeschreibung' : 'AI returned no description'),
        );
      }
    } catch (err) {
      img.removeAttribute('data-accessify-loading');
      img.setAttribute('data-accessify-error', 'true');
      img.setAttribute(
        'title',
        (lang() === 'de' ? 'Bildbeschreibung fehlgeschlagen' : 'Alt-text generation failed'),
      );
      console.warn('[Accessify] Alt-text generation failed:', src, err);
    }
  }

  // -------------------------------------------------------------------------
  // Dynamic image registration
  // -------------------------------------------------------------------------

  function tryRegisterImage(img: HTMLImageElement) {
    if (!enabled) return;
    if (!isValidImage(img)) return;
    if (missingAltImages.includes(img)) return;

    function addIfValid() {
      if (!enabled || missingAltImages.includes(img)) return;
      if (img.naturalWidth < 20 || img.naturalHeight < 20) return;

      if (needsAltText(img)) {
        missingAltImages.push(img);
        generateSingle(img);
      } else {
        // Image has alt but maybe no title/badge yet — add both
        const alt = img.getAttribute('alt');
        if (alt && alt.trim()) {
          if (!img.getAttribute('title')) {
            img.setAttribute('title', alt);
            img.setAttribute('data-accessify-title', 'auto');
          }
          addInfoBadge(img, alt.trim());
        }
      }
    }

    if (img.complete && img.naturalWidth > 0) addIfValid();
    else img.addEventListener('load', addIfValid, { once: true });
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  function activate() {
    if (enabled) return;
    enabled = true;

    // Recognize images that were auto-applied from cache/manifest
    document.querySelectorAll<HTMLImageElement>('img[data-accessify-alt="auto"]').forEach((img) => {
      if (img.closest('#accessify-root')) return;
      if (!processedImages.has(img)) {
        processedImages.set(img, { generatedAlt: img.getAttribute('alt') || '' });
        missingAltImages.push(img);
      }
    });

    // Scan for truly missing alt texts
    const missing = scanForMissingAlt();
    for (const img of missing) {
      if (!missingAltImages.includes(img)) {
        missingAltImages.push(img);
      }
    }

    // Start AI generation in background (img + background images)
    generateAll();
    generateBgAlts();

    // Set title on ALL images (consistent hover tooltip everywhere)
    applyTitlesToAllImages();

    // Add info badges to show alt text is available
    addBadgesToAllImages();

    // Register not-yet-loaded images
    document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (!img.complete) tryRegisterImage(img);
    });

    // Delayed re-scans for late-loading images and AI-generated alt texts
    for (const delay of [2000, 5000, 10000]) {
      setTimeout(() => {
        if (enabled) {
          document.querySelectorAll<HTMLImageElement>('img').forEach(tryRegisterImage);
          applyTitlesToAllImages();
          addBadgesToAllImages();
        }
      }, delay);
    }

    // Watch for new images
    domObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLImageElement) tryRegisterImage(node);
          else if (node instanceof HTMLElement) {
            node.querySelectorAll<HTMLImageElement>('img').forEach(tryRegisterImage);
          }
        }
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  function deactivate() {
    enabled = false;
    autoGenerating = false;
    domObserver?.disconnect();
    domObserver = null;

    // Remove auto-added titles (but keep alt texts — those are useful)
    removeTitles();
    missingAltImages = [];
  }

  // Run auto-apply immediately when the module is created
  autoApplyCachedAltTexts({ siteKey, proxyUrl, lang: initialLang }).catch(() => {});

  return {
    id: 'alt-text',
    name: () => (isDE() ? 'Bildbeschreibung' : 'Image Description'),
    description: isDE() ? 'Bildbeschreibungen per Hover anzeigen und fehlende automatisch erzeugen' : 'Show image descriptions on hover and auto-generate missing ones',
    icon: 'alt-text',
    category: 'ai',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'alt-text',
      enabled,
      value: {
        missingCount: missingAltImages.filter((img) => !processedImages.has(img)).length,
        processedCount: processedImages.size,
      },
    }),
  };
}
